import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import Card from '../components/common/Card'
import TootiDialog from '../components/common/Dialog'
import ListRow from '../components/common/ListRow'
import Switch from '../components/common/Switch'
import { logoutDialog } from '../lib/alerts'
import { faDigits } from '../lib/numerals'
import { weekdayInitials } from '../lib/progress'
import {
  DEFAULT_SPEEDS,
  EVERY_DAY,
  WEEKEND,
  WORKDAYS,
  autoThemeNow,
  clock24,
  micStatus,
  reminderSummary,
  speedLabel,
  stepMinutes,
  themeOptions,
} from '../lib/settings'
import { APP_VERSION, appVersionLine, faVersion } from '../lib/version'
import { listNotifications } from '../services/notifications'
import { useAuthStore } from '../store/authStore'
import { usePrefsStore } from '../store/prefsStore'
import { useThemeStore } from '../store/themeStore'
import { useToastStore } from '../store/toastStore'

const MIC_TONE = { good: 'text-good-ink', bad: 'text-bad-ink', muted: 'text-ink-3' }

const sameDays = (a, b) => a.length === b.length && a.every((on, index) => on === b[index])

/** The device's hour, re-read every minute so a page left open across 19:00 says so. */
function useHourOfDay() {
  const [hour, setHour] = useState(() => new Date().getHours())
  useEffect(() => {
    const timer = setInterval(() => setHour(new Date().getHours()), 60_000)
    return () => clearInterval(timer)
  }, [])
  return hour
}

/** The Permissions API's answer for the microphone, kept current; `unknown` where the browser has no answer. */
function useMicPermission() {
  const [state, setState] = useState('unknown')
  useEffect(() => {
    let status = null
    let cancelled = false
    const query = navigator.permissions?.query?.({ name: 'microphone' })
    if (!query) return undefined
    query
      .then((result) => {
        if (cancelled) return
        status = result
        setState(result.state)
        result.onchange = () => setState(result.state)
      })
      .catch(() => {})
    return () => {
      cancelled = true
      if (status) status.onchange = null
    }
  }, [])
  return state
}

function GroupLabel({ children }) {
  return <h2 className="text-xs font-semibold text-ink-3">{children}</h2>
}

function CardTitle({ children, className = '' }) {
  return <h3 className={`text-sm font-semibold text-ink ${className}`}>{children}</h3>
}

function Divider() {
  return <div className="h-px bg-line" />
}

function StepButton({ label, onClick, children }) {
  return (
    <button type="button" aria-label={label} onClick={onClick} className="grid size-8 place-items-center rounded-lg bg-surface-2 text-base font-bold text-ink-2">
      {children}
    </button>
  )
}

function ReminderPanel({ reminder, onChange }) {
  const { t } = useTranslation('settings')
  const weekdays = weekdayInitials()
  return (
    <div className="space-y-3 rounded-[14px] bg-surface-2 px-[13px] pb-[15px] pt-[13px]">
      <div className="flex items-center gap-2.5">
        <span className="flex-1 text-[12.5px] font-semibold text-ink-2">{t('page.reminderTime')}</span>
        <div className="flex items-center gap-2 rounded-[11px] border border-line bg-surface p-1" dir="ltr">
          <StepButton label={t('page.reminderQuarterEarlier')} onClick={() => onChange({ minuteOfDay: stepMinutes(reminder.minuteOfDay, -1) })}>
            −
          </StepButton>
          <span className="tnum min-w-14 text-center text-[15px] font-bold text-ink">{clock24(reminder.minuteOfDay)}</span>
          <StepButton label={t('page.reminderQuarterLater')} onClick={() => onChange({ minuteOfDay: stepMinutes(reminder.minuteOfDay, +1) })}>
            +
          </StepButton>
        </div>
      </div>

      <div className="space-y-2">
        <span className="block text-[12.5px] font-semibold text-ink-2">{t('page.weekdays')}</span>
        <div className="flex gap-1.5">
          {weekdays.map((initial, index) => {
            const on = reminder.days[index]
            return (
              <button
                key={initial}
                type="button"
                aria-pressed={on}
                onClick={() => onChange({ days: reminder.days.map((value, i) => (i === index ? !value : value)) })}
                className={`h-[38px] flex-1 rounded-[10px] text-[12.5px] font-semibold transition-colors ${
                  on ? 'bg-brand-600 text-white dark:text-slate-900' : 'bg-surface text-ink-3 ring-1 ring-line'
                }`}
              >
                {initial}
              </button>
            )
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {[
          { label: t('days.every'), days: EVERY_DAY },
          { label: t('days.workdays'), days: WORKDAYS },
          { label: t('days.weekend'), days: WEEKEND },
        ].map((preset) => {
          const active = sameDays(reminder.days, preset.days)
          return (
            <button
              key={preset.label}
              type="button"
              aria-pressed={active}
              onClick={() => onChange({ days: preset.days })}
              className={`h-8 rounded-full px-[11px] text-[11.5px] font-semibold transition-colors ${
                active ? 'bg-brand-50 text-brand-700' : 'bg-surface text-ink-2 ring-1 ring-line'
              }`}
            >
              {preset.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/** The two-way FA/EN switch (design's «زبان رابط» row), styled like the theme picker beside it. */
function LanguagePicker() {
  const { t } = useTranslation('common')
  const language = usePrefsStore((s) => s.language)
  const setLanguage = usePrefsStore((s) => s.setLanguage)
  return (
    <div className="grid grid-cols-2 gap-1.5 rounded-[11px] bg-surface-2 p-[3px]" role="radiogroup" aria-label={t('language.ariaLabel')}>
      {['fa', 'en'].map((value) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={language === value}
          onClick={() => setLanguage(value, 'manual')}
          className={`h-10 rounded-[9px] text-[12.5px] font-semibold transition-colors ${
            language === value ? 'bg-surface text-ink shadow-sm' : 'text-ink-2'
          }`}
        >
          {t(`language.${value}`)}
        </button>
      ))}
    </div>
  )
}

/**
 * The «زبان رابط» row, divider and all — absent rather than disabled when
 * the caller's country has settled the language for them
 * (`lib/geoLocale.js`). A switch that refuses to move reads as a bug; a row
 * that is not there reads as "this is the site you get", which is what an
 * Iranian visitor is actually being told.
 */
function LanguageRow() {
  const { t } = useTranslation('settings')
  const languageLocked = usePrefsStore((s) => s.languageLocked)
  if (languageLocked) return null
  return (
    <>
      <Divider />
      <CardTitle>{t('page.languageRow')}</CardTitle>
      <LanguagePicker />
    </>
  )
}

/**
 * Settings (design page 18, `settings`; P-settings of the go-live plan).
 * Every value is device-local — the theme store, the preferences store —
 * and each control writes one setting, so nothing is cached twice. The
 * rows whose targets are not built (monthly auto-pay, wave 5) are absent
 * rather than dead, and this client keeps no recordings on the device, so
 * the design's «حذف ضبط‌های ذخیره‌شده» row has nothing to delete and is
 * not drawn.
 */
export default function Settings() {
  const { t } = useTranslation('settings')
  const themeMode = useThemeStore((s) => s.mode)
  const setThemeMode = useThemeStore((s) => s.setMode)
  const defaultSpeed = usePrefsStore((s) => s.defaultSpeed)
  const setDefaultSpeed = usePrefsStore((s) => s.setDefaultSpeed)
  const reminder = usePrefsStore((s) => s.reminder)
  const setReminder = usePrefsStore((s) => s.setReminder)
  const signOut = useAuthStore((s) => s.signOut)
  const showToast = useToastStore((s) => s.show)
  const hour = useHourOfDay()
  const mic = micStatus(useMicPermission())
  const [unread, setUnread] = useState(0)
  const [askLogout, setAskLogout] = useState(false)

  useEffect(() => {
    let cancelled = false
    listNotifications()
      .then((data) => {
        if (!cancelled) setUnread(data.unreadCount ?? 0)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  // Turning the reminder on asks the browser for notifications once; a
  // refusal keeps the setting — the toast still rings — and says so.
  async function toggleReminder() {
    const next = !reminder.enabled
    setReminder({ enabled: next })
    if (next && typeof Notification !== 'undefined' && Notification.permission === 'default') {
      const answer = await Notification.requestPermission().catch(() => 'denied')
      if (answer !== 'granted') showToast(t('page.reminderPermissionDenied'))
    }
  }

  return (
    <div className="mx-auto max-w-md space-y-4">
      <header className="flex items-center gap-2">
        <Link to="/profile" aria-label={t('page.backToProfile')} className="grid size-11 place-items-center rounded-xl text-ink hover:bg-surface-2">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 6l6 6-6 6" />
          </svg>
        </Link>
        <h1 className="flex-1 text-lg font-bold text-ink">{t('page.title')}</h1>
      </header>

      {/* ── نمایش ── */}
      <section className="space-y-2.5">
        <GroupLabel>{t('page.display')}</GroupLabel>
        <Card as="div" pad="p-4" className="space-y-[13px]">
          <CardTitle>{t('page.themeCardTitle')}</CardTitle>
          <div className="grid grid-cols-3 gap-1.5 rounded-[11px] bg-surface-2 p-[3px]" role="radiogroup" aria-label={t('page.themeCardTitle')}>
            {themeOptions().map((option) => (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={themeMode === option.value}
                onClick={() => setThemeMode(option.value)}
                className={`h-10 rounded-[9px] text-[12.5px] font-semibold transition-colors ${
                  themeMode === option.value ? 'bg-surface text-ink shadow-sm' : 'text-ink-2'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] leading-[1.9] text-ink-3">
            {t('page.autoThemeLine')}{' '}
            <span dir="ltr" className="tnum font-bold text-ink-2">
              {autoThemeNow(hour)}
            </span>
          </p>

          <LanguageRow />
        </Card>
      </section>

      {/* ── تمرین ── */}
      <section className="space-y-2.5">
        <GroupLabel>{t('page.practice')}</GroupLabel>
        <Card as="div" pad="p-4" className="space-y-[13px]">
          <CardTitle>{t('page.defaultSpeed')}</CardTitle>
          <div className="flex gap-1.5" role="radiogroup" aria-label={t('page.defaultSpeed')}>
            {DEFAULT_SPEEDS.map((speed) => (
              <button
                key={speed}
                type="button"
                role="radio"
                aria-checked={defaultSpeed === speed}
                dir="ltr"
                onClick={() => setDefaultSpeed(speed)}
                className={`tnum h-[38px] flex-1 rounded-[9px] text-[12.5px] font-semibold transition-colors ${
                  defaultSpeed === speed ? 'bg-brand-600 text-white dark:text-slate-900' : 'bg-surface text-ink-2 ring-1 ring-line'
                }`}
              >
                {speedLabel(speed)}
              </button>
            ))}
          </div>

          <Divider />

          <div className="flex items-center gap-3">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <CardTitle>
                <span id="reminder-label">{t('page.dailyReminder')}</span>
              </CardTitle>
              <span className="text-[11.5px] text-ink-3">{reminderSummary(reminder)}</span>
            </div>
            <Switch checked={reminder.enabled} onToggle={toggleReminder} labelledBy="reminder-label" />
          </div>
          {reminder.enabled && <ReminderPanel reminder={reminder} onChange={setReminder} />}
          <p className="text-[11px] leading-[1.9] text-ink-3">{t('page.reminderWebNote')}</p>

          <Divider />

          <div className="flex min-h-11 items-center gap-3">
            <CardTitle className="flex-1">{t('page.micAccess')}</CardTitle>
            <span className={`text-xs font-semibold ${MIC_TONE[mic.tone]}`}>{mic.label}</span>
          </div>
          {mic.tone === 'bad' && <p className="text-[11px] leading-[1.9] text-ink-3">{t('page.micBlockedNote')}</p>}
        </Card>
      </section>

      {/* ── حساب ── */}
      <section className="space-y-2.5">
        <GroupLabel>{t('page.account')}</GroupLabel>
        <div className="space-y-2">
          <ListRow label={t('page.notificationsRow')} value={unread > 0 ? t('page.unreadSuffix', { count: faDigits(unread) }) : ''} to="/notifications" />
          <ListRow label={t('page.autopayRow')} to="/autopay" />
          <ListRow label={t('page.alertsRow')} to="/alerts" />
          <ListRow label={t('page.privacyRow')} />
          <ListRow label={t('page.appVersionRow')} value={faVersion(APP_VERSION)} quiet />
          <ListRow label={t('page.deleteAccountRow')} to="/delete-account" destructive />
          <ListRow label={t('page.logoutRow')} onClick={() => setAskLogout(true)} destructive />
        </div>
      </section>

      <p className="pt-1 text-center text-[11px] text-ink-3">{appVersionLine()}</p>

      {askLogout && (
        <TootiDialog
          spec={logoutDialog()}
          onConfirm={() => {
            setAskLogout(false)
            signOut()
          }}
          onDismiss={() => setAskLogout(false)}
        />
      )}
    </div>
  )
}
